import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hook';
import { Button, Card, Grid, Input, Result, Space, Table, TableProps, Tooltip, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { deactivateDevices, reset } from './_slice';
import { appConfig } from '../../../common/config';
import { convertDevicesToString, getFileNameBasedOnTime, parseSerialNumbers } from '../../../common/helper';
import CopyToClipboard from '../../../components/copy_clipboard';

const columns: TableProps<any>['columns'] = [
  {
    title: 'Device Serial',
    dataIndex: 'SerialNumber',
    key: 'SerialNumber',
    align: 'center',
    render: (val) => <>[{val}] <CopyToClipboard content={val} /></>,
  },
  {
    title: 'Network ID',
    dataIndex: 'NetworkId',
    key: 'NetworkId',
    align: 'center',
    render: (val) => val ? <>[{val}] <CopyToClipboard content={val} /></> : '',
  },
  {
    title: 'Unsubscribed from Network',
    dataIndex: 'NetworkUnsubscribeOk',
    key: 'NetworkUnsubscribeOk',
    align: 'center',
    render: (val, record) => {
      if (val) {
        return <span style={{ color: 'green' }}><CheckCircleOutlined color='green'/> Unsubscribed</span>
      }
      if (record.NetworkMsg === 'Never activated') {
        return <Tooltip title={appConfig.msgs.deactivationResultTip}><span style={{ color: 'red' }}><CloseCircleOutlined /> {record.NetworkMsg ?? record.PipMsg}</span></Tooltip>
      }
      return <span style={{ color: 'red' }}><CloseCircleOutlined /> {record.NetworkMsg ?? record.PipMsg}</span>
    },
  },
  {
    title: 'Removed from Losant',
    dataIndex: 'LosantRemoveOk',
    key: 'LosantRemoveOk',
    align: 'center',
    render: (val, record) => {
      if (val) {
        return <span style={{ color: 'green' }}><CheckCircleOutlined color='green'/> Removed</span>
      }
      return <span style={{ color: 'red' }}><CloseCircleOutlined /> {record.LosantMsg ?? record.PipMsg}</span>
    },
  },
];

interface IDevicesDeactivationProps {
  serialNumbers?: string;
}

const DevicesDeactivation: React.FC<IDevicesDeactivationProps> = () => {
  const devicesDeactivationState = useAppSelector((state) => state.devicesDeactivation);
  const dispatch = useAppDispatch();
  const [serials, setSerials] = useState('');

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const cardSize = isMobile ? 'small' : 'default';

  const devices = devicesDeactivationState.result?.Devices || [];

  const exportToCSV = (devices: any[]) => {
    const csv = convertDevicesToString(devices);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', getFileNameBasedOnTime("PiP_Deactivation_Results_"));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Space direction='vertical' size="middle" style={{ display: 'flex' }}>
      <Card size={cardSize} variant="borderless">
        <Space direction='vertical' size="middle" style={{ display: 'flex' }}>
          <Input.TextArea
            rows={6}
            value={serials}
            onChange={(e: any) => setSerials(e.target.value)}
            placeholder={appConfig.msgs.serialsInputPlaceholder}
          />
          <Typography.Text type="danger">{appConfig.msgs.deactivationTip}</Typography.Text>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            {
              <Button
                size='large'
                style={{ width: 160 }}
                onClick={() => {
                  setSerials('');
                  dispatch(reset());
                }}
              >
                Clear
              </Button>
            }
            <Button
              type="primary"
              size='large'
              style={{ width: 160 }}
              onClick={() => {
                const serialNumbers = parseSerialNumbers(serials);
                setSerials(serialNumbers.join('\n'));
                dispatch(deactivateDevices({ serialNumbers }));
              }}
              loading={devicesDeactivationState.isLoading}
            >
              Deactivate
            </Button>
          </div>
        </Space>
      </Card>

      {
        devicesDeactivationState.errMsg && (
          <Result
            status="warning"
            title={devicesDeactivationState.errMsg}
          />
        )
      }

      {
        devices.length > 0 && (
          <Card
            size={cardSize}
            title={`Deactivation Results (${devices.length})`}
            variant="borderless"
            extra={<Button onClick={() => exportToCSV(devices)}>Export to CSV file</Button>}
          >
            <Table<any>
              columns={columns}
              dataSource={devices}
              rowKey='SerialNumber'
              pagination={{ pageSize: 1000 }}
              loading={devicesDeactivationState.isLoading}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        )
      }
    </Space>
  );
};

export default DevicesDeactivation;

